import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetFootprintingPorkPrimaryDataById,
  useMutateFootprintingPorkPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_PORK_FOOTPRINTING_MOORLAND_ACTIVE, FARM_FOOTPRINTING_PORK_MOORLAND } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin, Typography } from "components";

interface MoorlandProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const PorkMoorlandForm = ({ primaryDataId, readOnly, farmId, year }: MoorlandProps) => {
  const { t } = useTranslation("footprintingPork");
  const formRef = useRef<FormInstance>(null);

  const [moorlandActive, setMoorlandActive] = useState<ENUM_PORK_FOOTPRINTING_MOORLAND_ACTIVE | null>(null);
  const [moorlandAreaForFodderProduction, setFodderProductionAreaHa] = useState<number | null>(null);
  const [moorlandAnnualWaterLevel, setannualWaterLevelCm] = useState<number | null>(null);
  const pendingValidateRef = useRef(false);

  const { data: porkPrimaryData, isLoading, isSuccess } = useGetFootprintingPorkPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutatePorkPrimaryData } = useMutateFootprintingPorkPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      moorlandActive,
      moorlandAreaForFodderProduction,
      moorlandAnnualWaterLevel,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePorkPrimaryData({});
      }
    },
    [isLoading, mutatePorkPrimaryData, readOnly]
  );

  useEffect(() => {
    if (porkPrimaryData && isSuccess) {
      setMoorlandActive(porkPrimaryData.moorlandActive ?? null);
      setFodderProductionAreaHa(porkPrimaryData.moorlandAreaForFodderProduction ?? null);
      setannualWaterLevelCm(porkPrimaryData.moorlandAnnualWaterLevel ?? null);

      formRef.current?.setFieldsValue({
        fodderProductionAreaHa: porkPrimaryData.moorlandAreaForFodderProduction ?? null,
        annualWaterLevelCm: porkPrimaryData.moorlandAnnualWaterLevel ?? null,
      });

      if (porkPrimaryData.moorlandActive === FARM_FOOTPRINTING_PORK_MOORLAND.YES) {
        pendingValidateRef.current = true;
      }
    }
  }, [porkPrimaryData, isSuccess]);

  // Fires on the render where moorlandActive actually becomes "yes" (i.e. after the state update
  // from the load effect above has committed and the gated fields have mounted) — not in the same
  // effect-flush pass as that update, which is too early for the fields to be registered yet.
  useEffect(() => {
    if (!pendingValidateRef.current) return;
    if (moorlandActive !== FARM_FOOTPRINTING_PORK_MOORLAND.YES) return;
    pendingValidateRef.current = false;
    formRef.current?.validateFields();
  }, [moorlandActive]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-pork-moorland-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={24}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("porkMoorlandForm.question.moorlandRadioQuestion")}
              {true && <span className="required-asterisk">*</span>}
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-pork-moorland-radio"
              onChange={({ target }) => {
                setMoorlandActive(target.value);
              }}
              value={moorlandActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_PORK_MOORLAND.YES, FARM_FOOTPRINTING_PORK_MOORLAND.NO].map((moorland) => ({
                label: t(`porkMoorlandForm.moorlandOptions.${moorland}`),
                value: moorland,
              }))}
            />
          </Flex>

          {moorlandActive === FARM_FOOTPRINTING_PORK_MOORLAND.YES && (
            <Flex vertical gap={16}>
              <Flex vertical>
                <InputNumber
                  disabled={readOnly}
                  name="fodderProductionAreaHa"
                  dataTestId="footprinting-pork-fodder-production-area-input"
                  label={t("porkMoorlandForm.question.fodderProductionAreaLabel")}
                  suffix={t("porkMoorlandForm.question.fodderProductionAreaSuffix")}
                  value={moorlandAreaForFodderProduction}
                  initialValue={moorlandAreaForFodderProduction}
                  onChange={(v) => setFodderProductionAreaHa(v)}
                  validateFirst
                  rules={[
                    { required: true, message: t("porkMoorlandForm.error.validNumber") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                          return Promise.reject(new Error(t("porkMoorlandForm.error.validNumber")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t("porkMoorlandForm.notSure")}
                </Typography>
              </Flex>

              <Flex vertical>
                <InputNumber
                  disabled={readOnly}
                  name="annualWaterLevelCm"
                  dataTestId="footprinting-pork-annual-water-lever-input"
                  label={t("porkMoorlandForm.question.annualWaterLevelLabel")}
                  suffix={t("porkMoorlandForm.question.annualWaterLevelSuffix")}
                  value={moorlandAnnualWaterLevel}
                  initialValue={moorlandAnnualWaterLevel}
                  onChange={(v) => setannualWaterLevelCm(v)}
                  validateFirst
                  rules={[
                    { required: true, message: t("porkMoorlandForm.error.validNumber") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                          return Promise.reject(new Error(t("porkMoorlandForm.error.validNumber")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t("porkMoorlandForm.notSure")}
                </Typography>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

PorkMoorlandForm.Key = "pork.moorland";
export { PorkMoorlandForm };
